/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 * EVENTSFLO-ADMIN-BACKUP-UI-COPY-JSON-V1 — added compact "Copy JSON"
 * button to JsonBlock. Copies exactly the displayed/sanitised payload
 * (the same string the <pre> renders). Uses navigator.clipboard when
 * available, falls back to document.execCommand('copy') via a hidden
 * textarea. Sonner toast on success/failure. Button is hidden when
 * `data` is null/undefined so it never appears before lazy-load.
 *
 * Pure formatting helpers + shared JSON renderer used by the backups
 * sub-components.
 */
import React from "react";
import { Button } from "../../../components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "sonner";

export const PAGE_SIZE = 25;

export function formatBytes(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return String(iso);
  }
}

// Copies `text` using the Clipboard API when available, otherwise
// falls back to a hidden textarea + document.execCommand('copy').
// Resolves true on success, false otherwise.
async function copyTextSafe(text) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_e) {
    return false;
  }
}

export function JsonBlock({ data, copyTestId }) {
  // The exact string rendered inside <pre> — same input goes to the
  // clipboard so the copied payload is always equal to what the user
  // sees on screen.
  const rendered = JSON.stringify(data, null, 2);
  const canCopy = data !== null && data !== undefined;

  const onCopy = async () => {
    const ok = await copyTextSafe(rendered);
    if (ok) {
      toast.success("JSON copied");
    } else {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="relative">
      {canCopy && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCopy}
          data-testid={copyTestId || "copy-json-btn"}
          className="absolute top-2 right-2 h-7 px-2 text-xs"
        >
          <Copy className="w-3 h-3 mr-1" /> Copy JSON
        </Button>
      )}
      <pre
        data-testid="json-block"
        className="text-xs bg-zinc-900 text-zinc-100 p-3 pr-24 rounded overflow-auto max-h-[60vh] whitespace-pre-wrap break-words"
      >
        {rendered}
      </pre>
    </div>
  );
}
